import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { scholarPortalAPI } from '../../services/api';
import '../../styles/components.css';

const Dashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
    const [scholarProfile, setScholarProfile] = useState(null);
    const [publicationCount, setPublicationCount] = useState(0);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const getNavigationLinks = () => {
        return [
            { path: '/scholar/dashboard', label: 'Dashboard' },
            { path: '/scholar/publications', label: 'My Publications' },
            { path: '/scholar/update-bank-details', label: 'Update Bank Details' },
            { path: '/scholar/monthly-claim', label: 'Monthly Claim Form' },
        ];
    };

    const navLinks = getNavigationLinks();

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        // Fetch scholar profile and live publications count from backend
        const fetchDashboardData = async () => {
            try {
                const response = await scholarPortalAPI.getProfile();
                if (response.data && response.data.success) {
                    setScholarProfile(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching scholar profile:', error);
            }

            try {
                const pubResponse = await scholarPortalAPI.getMyPublications();
                if (Array.isArray(pubResponse.data)) {
                    setPublicationCount(pubResponse.data.length);
                }
            } catch (error) {
                console.error('Error fetching publications count:', error);
            }
        };
        fetchDashboardData();
    }, [user, navigate]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) {
                setSidebarOpen(true);
            } else {
                setSidebarOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMobile && sidebarOpen) {
                const sidebar = document.querySelector('.sidebar');
                const hamburger = document.querySelector('.hamburger-menu');
                if (sidebar && !sidebar.contains(event.target) && hamburger && !hamburger.contains(event.target)) {
                    setSidebarOpen(false);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMobile, sidebarOpen]);

    return (
        <div className="layout">
            {/* Sidebar */}
            <aside
                className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}
                style={isMobile ? {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    height: '100vh',
                    zIndex: 1000,
                    width: '250px',
                    transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
                    transition: 'transform 0.3s ease-in-out',
                    boxShadow: '2px 0 5px rgba(0,0,0,0.1)'
                } : {}}
            >
                <div className="sidebar-header">
                    <h2>TRF System</h2>
                    {!isMobile && (
                        <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
                            {sidebarOpen ? '◀' : '▶'}
                        </button>
                    )}
                    {isMobile && (
                        <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', marginLeft: 'auto', color: 'white' }}>
                            ✕
                        </button>
                    )}
                </div>

                <nav className="sidebar-nav">
                    {navLinks.map((link) => (
                        link.isExternal ? (
                            <a
                                key={link.path}
                                href={link.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                            >
                                {sidebarOpen && <span className="nav-label">{link.label}</span>}
                            </a>
                        ) : (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                            >
                                {sidebarOpen && <span className="nav-label">{link.label}</span>}
                            </Link>
                        )
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <div className="user-info">
                        {sidebarOpen && (
                            <>
                                <p className="user-name">{scholarProfile?.name || user?.fullName || ''}</p>
                                <p className="user-role">{user?.role?.toUpperCase()}</p>
                            </>
                        )}
                    </div>
                    <button className="logout-btn" onClick={handleLogout}>
                        {sidebarOpen && 'Logout'}
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className={`main-content ${sidebarOpen && !isMobile ? '' : 'expanded'}`}>
                <header className="content-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {isMobile && (
                        <button
                            className="hamburger-menu"
                            onClick={() => setSidebarOpen(true)}
                            style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
                        >
                            ☰
                        </button>
                    )}
                    <h1>Dashboard</h1>
                </header>

                <div className="content-body">
                    <div className="stats-grid">
                        <div className="stat-card" style={{ background: 'linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)', color: 'white' }}>
                            <div className="stat-value" style={{ fontSize: '2rem', fontWeight: 700, color: 'white' }}>
                                {publicationCount}
                            </div>
                            <div className="stat-label" style={{ fontSize: '0.875rem', color: 'white', marginBottom: '0.5rem' }}>Total No of Publications</div>
                        </div>
                        <div className="stat-card" style={{ background: 'linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)', color: 'white' }}>
                            <div className="stat-value" style={{ fontSize: '2rem', fontWeight: 700, color: 'white' }}>Rs. {scholarProfile?.tclaim || 0}</div>
                            <div className="stat-label" style={{ fontSize: '0.875rem', color: 'white', marginBottom: '0.5rem' }}>Total Claim Received</div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Dashboard;